import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, X, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const POLL_INTERVAL_MS = 60_000;

const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}sn`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}dk`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}sa`;
  return `${Math.floor(diffSec / 86400)}g`;
};

export default function NotificationBell({ align = "up-left" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/count");
      setCount(data?.unread || 0);
    } catch { /* silent */ }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  // Initial load + polling
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // Open dropdown → refresh list
  useEffect(() => {
    if (open) { loadItems(); loadCount(); }
  }, [open]);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const markRead = async (nid, opts = {}) => {
    setItems((prev) => prev.map(n => n.id === nid ? { ...n, read_at: n.read_at || new Date().toISOString() } : n));
    setCount((c) => Math.max(0, c - 1));
    try { await api.post(`/notifications/${nid}/read`); } catch { /* silent */ }
    if (opts.reloadCount !== false) loadCount();
  };

  const softDelete = async (nid, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setItems((prev) => prev.filter(n => n.id !== nid));
    try { await api.delete(`/notifications/${nid}`); } catch { /* silent */ }
    loadCount();
  };

  const readAll = async () => {
    setMarkingAll(true);
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setCount(0);
    } catch { /* silent */ }
    finally { setMarkingAll(false); }
  };

  const onClickItem = async (n) => {
    if (!n.read_at) await markRead(n.id, { reloadCount: false });
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        data-testid="notification-bell"
        aria-label="Bildirimler"
        onClick={() => setOpen(v => !v)}
        className="relative flex items-center justify-center w-9 h-9 rounded-sm border border-white/10 hover:border-brand-red hover:text-brand-red text-neutral-300 transition-colors"
      >
        <Bell className="w-4 h-4" strokeWidth={1.75} />
        {count > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[10px] font-mono flex items-center justify-center leading-none"
          >{count > 99 ? "99+" : count}</span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className={cn(
            "absolute w-[380px] max-w-[85vw] max-h-[70vh] flex flex-col bg-[#0d0d0d] border border-white/10 rounded-sm shadow-2xl z-50",
            align === "down-right" && "top-full right-0 mt-2",
            align === "up-left" && "bottom-full left-0 mb-2",
          )}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div>
              <div className="font-display uppercase text-sm tracking-widest">Bildirimler</div>
              <div className="text-[10px] text-neutral-500">{count} okunmamış</div>
            </div>
            <button
              data-testid="notification-read-all"
              onClick={readAll}
              disabled={markingAll || count === 0}
              className="text-[10px] uppercase tracking-widest text-brand-red hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed flex items-center gap-1"
            >
              <CheckCheck className="w-3 h-3" /> Hepsini okundu işaretle
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div data-testid="notification-loading" className="p-6 text-center text-xs text-neutral-500">Yükleniyor…</div>
            ) : items.length === 0 ? (
              <div data-testid="notification-empty" className="p-10 text-center">
                <div className="relative w-12 h-12 mx-auto mb-4">
                  <div className="absolute inset-0 -m-3 rounded-full bg-brand-red/5 blur-2xl" />
                  <div className="relative w-full h-full rounded-sm border border-white/10 bg-black/40 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-neutral-500" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="font-display uppercase text-sm tracking-tight text-white/90">Henüz bildirim yok</div>
                <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed max-w-[280px] mx-auto">
                  Akış başladığında tüm hareketler burada listelenecek.
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map((n) => {
                  const isUnread = !n.read_at;
                  return (
                    <li key={n.id} data-testid={`notification-item-${n.id}`} className={cn(
                      "group px-4 py-3 hover:bg-white/5 cursor-pointer relative",
                      isUnread && "bg-brand-red/5"
                    )} onClick={() => onClickItem(n)}>
                      <div className="flex items-start gap-2">
                        {isUnread && <span className="mt-1.5 w-2 h-2 rounded-full bg-brand-red shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className={cn("text-sm truncate", isUnread ? "text-white" : "text-neutral-400")}>
                            {n.title || n.event_type}
                          </div>
                          {n.body && (
                            <div className={cn("text-xs mt-0.5 line-clamp-2", isUnread ? "text-neutral-300" : "text-neutral-500")}>
                              {n.body}
                            </div>
                          )}
                          <div className="text-[10px] text-neutral-600 mt-1 font-mono">{relTime(n.created_at)} önce</div>
                        </div>
                        <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isUnread && (
                            <button
                              type="button"
                              data-testid={`notification-mark-read-${n.id}`}
                              onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                              className="w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-emerald-400"
                              title="Okundu"
                            ><Check className="w-3.5 h-3.5" /></button>
                          )}
                          <button
                            type="button"
                            data-testid={`notification-delete-${n.id}`}
                            onClick={(e) => softDelete(n.id, e)}
                            className="w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-red-400"
                            title="Sil"
                          ><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
